import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, X, Check } from "lucide-react";

/**
 * Global confirm/prompt provider — a portable replacement for
 * window.confirm() and window.prompt() which are BLOCKED inside
 * sandboxed iframes (Emergent Preview panel among others).
 *
 * Usage:
 *   const { confirm, promptInput } = useConfirm();
 *   if (!(await confirm("Delete X?"))) return;
 *   const val = await promptInput("Enter target email", "default@x.pl");
 */

const ConfirmCtx = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  // { kind: 'confirm' | 'prompt', message, defaultValue?, tone, confirmLabel, cancelLabel, resolve }
  const inputRef = useRef(null);
  const [inputVal, setInputVal] = useState("");

  const confirm = useCallback(
    (message, opts = {}) =>
      new Promise((resolve) => {
        setState({
          kind: "confirm",
          message,
          tone: opts.tone || "warn",
          confirmLabel: opts.confirmLabel || "Potwierdź",
          cancelLabel: opts.cancelLabel || "Anuluj",
          resolve,
        });
      }),
    []
  );

  const promptInput = useCallback(
    (message, defaultValue = "", opts = {}) =>
      new Promise((resolve) => {
        setInputVal(defaultValue || "");
        setState({
          kind: "prompt",
          message,
          defaultValue,
          tone: opts.tone || "info",
          confirmLabel: opts.confirmLabel || "OK",
          cancelLabel: opts.cancelLabel || "Anuluj",
          resolve,
        });
      }),
    []
  );

  const close = (value) => {
    if (state) state.resolve(value);
    setState(null);
    setInputVal("");
  };

  useEffect(() => {
    if (!state) return;
    // Focus the input or the confirm button after mount
    const t = setTimeout(() => {
      if (state.kind === "prompt" && inputRef.current) inputRef.current.focus();
    }, 30);
    const onKey = (e) => {
      if (e.key === "Escape") close(state.kind === "prompt" ? null : false);
      if (e.key === "Enter" && state.kind === "confirm") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [state]);

  const tone = state?.tone || "warn";
  const toneStyle = {
    danger: { icon: "text-red-600", btn: "bg-red-600 hover:bg-red-700 text-white" },
    warn:   { icon: "text-amber-600", btn: "bg-zinc-900 hover:bg-zinc-800 text-white" },
    info:   { icon: "text-brand", btn: "bg-brand hover:opacity-90 text-white" },
  }[tone] || { icon: "text-amber-600", btn: "bg-zinc-900 text-white" };

  return (
    <ConfirmCtx.Provider value={{ confirm, promptInput }}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          data-testid="confirm-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) close(state.kind === "prompt" ? null : false);
          }}
        >
          <div className="bg-white border-2 border-zinc-900 shadow-[8px_8px_0_0_#18181b] max-w-md w-full">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900 bg-zinc-50">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`w-4 h-4 ${toneStyle.icon}`} />
                <div className="font-label text-xs">// Potwierdzenie</div>
              </div>
              <button
                onClick={() => close(state.kind === "prompt" ? null : false)}
                className="p-1 hover:bg-zinc-200"
                data-testid="confirm-close"
                aria-label="Zamknij"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-5">
              <div className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">
                {state.message}
              </div>
              {state.kind === "prompt" && (
                <input
                  ref={inputRef}
                  type="text"
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") close(inputVal);
                  }}
                  className="mt-4 w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2 text-sm font-mono"
                  data-testid="confirm-prompt-input"
                />
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-200 bg-zinc-50">
              <button
                onClick={() => close(state.kind === "prompt" ? null : false)}
                data-testid="confirm-cancel"
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
              >
                {state.cancelLabel}
              </button>
              <button
                onClick={() => close(state.kind === "prompt" ? inputVal : true)}
                data-testid="confirm-ok"
                className={`inline-flex items-center gap-1 px-4 py-2 text-xs font-bold uppercase tracking-wider ${toneStyle.btn}`}
              >
                <Check className="w-3 h-3" /> {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}
